import '../../../shared/StyledInput.css';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import FieldHelp from '../../../shared/FieldHelp';
import { formatPercent } from '../../../shared/numberFormat';

const sectionStyle = { padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };

// Accounting Settings: the tenant's one Default Tax Code.
function AccountingSetupPage({ onNavigate }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [taxCodes, setTaxCodes] = useState([]);
  const [defaultTaxCodeId, setDefaultTaxCodeId] = useState('');
  const [savedTaxCodeId, setSavedTaxCodeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    axios.get('/api/tax-codes', { params: { status: 'active' } })
      .then((taxRes) => {
        const rows = taxRes.data.data || [];
        setTaxCodes(rows);
        const current = rows.find((tc) => tc.is_default);
        const currentId = current ? String(current.id) : '';
        setDefaultTaxCodeId(currentId);
        setSavedTaxCodeId(currentId);
      })
      .catch((err) => showError(err, 'Failed to load Accounting Settings'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!defaultTaxCodeId) {
      showError(null, 'Select a Tax Code to set as default.');
      return;
    }
    setSaving(true);
    setSuccess('');
    try {
      if (defaultTaxCodeId !== savedTaxCodeId) {
        await axios.patch(`/api/tax-codes/${defaultTaxCodeId}/set-default`);
        setSavedTaxCodeId(defaultTaxCodeId);
      }
      setSuccess('Accounting Settings saved.');
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setSuccess('');
    if (onNavigate) onNavigate('accounting-dashboard');
  };

  if (loading) {
    return <div style={{ width: '100%', maxWidth: 760, margin: '32px auto' }}>Loading...</div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: 760, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Accounting Settings</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>The default Tax Code.</p>

      <form onSubmit={handleSave} className="accounting-setup-form" style={{ marginTop: 16 }}>
        <div style={sectionStyle}>
          <h4 style={sectionHeaderStyle}>Tax</h4>
          <label>Default Tax Code</label><br />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <select className="styled-input"
              value={defaultTaxCodeId}
              onChange={(e) => setDefaultTaxCodeId(e.target.value)}
              style={{ width: '100%', maxWidth: 480, boxSizing: 'border-box' }}
            >
              <option value="">(none)</option>
              {taxCodes.map((tc) => (
                <option key={tc.id} value={tc.id}>{tc.code} — {tc.description} ({formatPercent(tc.rate)})</option>
              ))}
            </select>
            <FieldHelp fieldKey="accounting-setup.default-tax-code" pageKey="accounting-setup" label="Default Tax Code">
              The Tax Code used wherever no other one applies: new Tax Handling Types, Pricelist GST, and Price Lookup for a Customer Price Group or All Customers. A specific Customer's own Tax Code always takes priority.
            </FieldHelp>
            {onNavigate && (
              <button
                type="button"
                onClick={() => onNavigate('accounting-tax')}
                style={{ marginLeft: 4, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0, whiteSpace: 'nowrap' }}
              >
                Manage Tax Codes
              </button>
            )}
          </div>
        </div>

        {success && <div style={{ color: '#2e7d32', marginTop: 12 }}>{success}</div>}

        <div style={{ marginTop: 16 }}>
          <SaveButton loading={saving} />
          <CancelButton onClick={handleCancel} disabled={saving} style={{ marginLeft: 8 }} />
        </div>
      </form>
    </div>
  );
}

export default AccountingSetupPage;
